/**
 * Self-contained reader + mapper for paramount's stateless filters.
 *
 * Reads the active filters from `sessionStorage` (under the same key paramount
 * uses) and converts them into the request shape that the assets-search-svc
 * endpoints under `search/assets` expect:
 *
 *   {
 *     filters:    string[],   // -> POST body `filters[]`
 *     searchTerms?: string    // -> POST body `searchTerms`
 *   }
 *
 * This file has NO runtime dependencies — it's intentionally self-contained.
 * The mapping mirrors paramount's session path (`src/assets/overview/`):
 * `getFiltersParamsFromSession` in `apis/utils.api.ts` (with the per-type
 * `filterTypeRegistry[*].toQueryParams` in `store/filter-behaviour/registry.ts`)
 * for `filters`, and `buildSearchTermQueryClauses` for `searchTerms`. Its
 * split-on path (`getFiltersParamsFromUrl`, id-based metaproperty clauses) is
 * mirrored in `statelessFiltersFromUrl.ts`.
 * `__tests__/statelessFiltersGoldenFixture.test.ts` enforces the sync: its
 * fixture is byte-identical to paramount's and asserted on both sides.
 *
 * Similarity assetIds: in paramount these come from the live search response
 * (`state.overview.assets`), but in session storage they're persisted ON the
 * similarity FilteringOption itself — for `fieldName: 'similarToAssetIds'`
 * the `name` field holds the asset id. One option per asset id. This mapper
 * collects them and emits a single `id:(id1 OR id2 …)` clause, matching
 * paramount's `getAssetIdsForSimilarityFiltering`. This is the one deliberate
 * difference: paramount doesn't put similarity in `filters` on this path.
 *
 * NOT handled here (left to the caller):
 *  - The `pendingBackendSync` (stateful) path. Everything here is stateless.
 */

// ---------------------------------------------------------------------------
//  Constants
// ---------------------------------------------------------------------------

const ACTIVE_FILTERS_LOCAL_STORAGE_KEY = 'activeFilters';

const ADVANCED_FILTER_DATE_FIELDS = ['dateCreated', 'datePublished', 'scheduledArchiveDate'];

const RESOLUTION_RANGES: Record<string, string> = {
	small: '[1 TO 199]',
	medium: '[200 TO 799]',
	large: '[800 TO *]',
};

const DURATION_RANGES: Record<string, string> = {
	short: '[0 TO 299]',
	medium: '[300 TO 1199]',
	long: '[1200 TO *]',
};

const SIMILAR_TO_ASSET_IDS = 'similarToAssetIds';

// ---------------------------------------------------------------------------
//  Types  (minimal structural subset of paramount's `FilteringOption`)
// ---------------------------------------------------------------------------

export type SerializedFilterDateType = { from: string; to: string };

// The search options ("Search in" Title / Description) and the operator that
// joins a multi-word term's words (`AND` when the SearchAndOperator ECA is on).
// Declared here, not in `helpers/types.ts` (which re-exports them), so this
// file stays self-contained. Values match paramount's `SearchOptionType` /
// `SearchOperator`.
export enum SearchOptionType {
	Title = 'title',
	Description = 'description',
}

export type SelectedSearchOption = { key: SearchOptionType };

export type SearchOperator = 'AND' | 'OR';

export enum FilteringOptionType {
	smartFilter = 'smartFilter',
	advancedFilter = 'advancedFilter',
	colorFilter = 'colorFilter',
	tagsFilter = 'tagsFilter',
	searchTerm = 'searchTerm',
	similarityFilter = 'similarityFilter',
}

export type FilteringOption = {
	type: FilteringOptionType | string;
	name: string | SerializedFilterDateType;
	label?: string;
	fieldName: string;
	fieldId?: string;
	id?: string;
	icon?: string;
	isMultifilter?: boolean;
	isBulkSearch?: boolean;
	rangeValue?: string[];
	// `searchTerm`-only fields
	searchTerm?: string;
	searchOptions?: SelectedSearchOption[];
};

export type FiltersParameterForPostRequest = {
	filters: string[];
	searchTerms?: string;
};

// ---------------------------------------------------------------------------
//  Page-type key  (mirrors paramount's `getActiveFiltersPageTypeKey`)
// ---------------------------------------------------------------------------

const getCollectionIdFromUrl = (pathname: string): string => {
	if (pathname.startsWith('/collections/view/')) {
		return pathname.split('/')[3] ?? '';
	}

	if (pathname.startsWith('/web/')) {
		return pathname.split('/')[2] ?? '';
	}

	if (pathname.startsWith('/share/')) {
		return pathname.split('/')[2] ?? '';
	}

	if (pathname.startsWith('/show/')) {
		return pathname.split('/')[3] ?? '';
	}

	return '';
};

const getActiveFiltersPageTypeKey = (): string => {
	const id = getCollectionIdFromUrl(window.location.pathname);

	return id.length ? id : 'media';
};

// ---------------------------------------------------------------------------
//  Generic helpers
// ---------------------------------------------------------------------------

const isDateSmartFilter = (name: FilteringOption['name']): name is SerializedFilterDateType => {
	return typeof name === 'object' && name !== null && 'from' in name && 'to' in name;
};

const formatMetapropertyName = (name: string): string => {
	const stripped = name.replaceAll(' ', '');

	if (stripped.startsWith('metaproperty_')) {
		return stripped;
	}

	return `metaproperty_${stripped}`;
};

const formatTagValue = (v: string): string => `"${v}"`;

// Values starting with one of these are quoted, since a leading reserved char
// (eg. `-test`) is otherwise misread as a query operator. Other values stay
// as-is — some are literal backend sentinels (eg. `NULL`) that must stay
// unquoted. Mirrors paramount's `NEEDS_QUOTING_REGEX`.
const NEEDS_QUOTING_REGEX = /^[-+!(){}[\]^"~*?:\\/&|]/;

const formatSmartFilterValue = (value: string): string => {
	if (!NEEDS_QUOTING_REGEX.test(value)) {
		return value;
	}

	// Escape so an embedded `"` or `\` can't break out of the quoted phrase.
	return `"${value.replace(/[\\"]/g, '\\$&')}"`;
};

// The user's local calendar day, like paramount's `format(date, 'yyyy-MM-dd')`:
// the date picker stores local midnight, so the UTC day can be the day before.
const toLocalDay = (value: string): string => {
	const date = new Date(value);
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	return `${date.getFullYear()}-${month}-${day}`;
};

/** `[<from day>T00:00:00Z TO <to day>T00:00:00Z]`, paramount's `buildDateRangeValue`. */
export const buildDateRangeValue = (date: SerializedFilterDateType): string =>
	`[${toLocalDay(date.from)}T00:00:00Z TO ${toLocalDay(date.to)}T00:00:00Z]`;

const buildDateMetafieldString = (
	metapropertyName: string,
	date: SerializedFilterDateType,
): string => `metafield_${metapropertyName}:${buildDateRangeValue(date)}`;

const formatMetapropertyOptionsForBulkSearch = (value: string): string => {
	return value
		.split(/[\s,]+/)
		.map((s) => s.trim())
		.filter(Boolean)
		.join(' OR ');
};

const buildBulkSearchString = (metapropertyName: string, value: string): string => {
	if (!metapropertyName || !value) {
		return '';
	}

	if (value.startsWith('metaproperty_')) {
		return value;
	}

	return `metaproperty_${metapropertyName}:(${formatMetapropertyOptionsForBulkSearch(value)})`;
};

// ---------------------------------------------------------------------------
//  Advanced-filter helpers (dates, ranges)
//  Ported from `components/filters/advanced/utils/{dates,ranges}.ts`.
// ---------------------------------------------------------------------------

const toUtcDayRange = (fromLocal: Date, toLocal: Date): string => {
	const from = new Date(fromLocal);
	from.setHours(0, 0, 0, 0);
	const to = new Date(toLocal);
	to.setHours(23, 59, 59, 0);
	const fromIso = from.toISOString().replace(/\.\d{3}Z$/, 'Z');
	const toIso = to.toISOString().replace(/\.\d{3}Z$/, 'Z');

	return `[${fromIso} TO ${toIso}]`;
};

const getDateParamFromAdvancedOption = (option: FilteringOption): string | undefined => {
	if (option.name === 'lastday') {
		const today = new Date();

		return toUtcDayRange(today, today);
	}

	if (option.name === 'lastweek') {
		const to = new Date();
		const from = new Date();
		from.setDate(from.getDate() - 6);

		return toUtcDayRange(from, to);
	}

	if (option.name === 'next7days') {
		const from = new Date();
		const to = new Date();
		to.setDate(to.getDate() + 6);

		return toUtcDayRange(from, to);
	}

	if (option.name === 'next31days') {
		const from = new Date();
		const to = new Date();
		to.setDate(to.getDate() + 30);

		return toUtcDayRange(from, to);
	}

	if (option.rangeValue && option.rangeValue.length >= 2) {
		const from = new Date(option.rangeValue[0]);
		const to = new Date(option.rangeValue[1]);

		return toUtcDayRange(from, to);
	}

	return undefined;
};

const getRangeParamFromResolution = (
	optionName: string,
	rangeValue?: string[],
): string | undefined => {
	if (optionName === 'custom' && rangeValue) {
		return `[${rangeValue[0]} TO ${rangeValue[1]}]`;
	}

	return RESOLUTION_RANGES[optionName];
};

const getRangeParamFromDuration = (
	optionName: string,
	rangeValue?: string[],
): string | undefined => {
	if (optionName === 'custom' && rangeValue) {
		return `[${rangeValue[0]} TO ${rangeValue[1]}]`;
	}

	return DURATION_RANGES[optionName];
};

const advancedOptionToQueryParam = (option: FilteringOption): string | undefined => {
	const { fieldName, name, rangeValue } = option;

	if (typeof name !== 'string') {
		return undefined;
	}

	if (ADVANCED_FILTER_DATE_FIELDS.includes(fieldName)) {
		const value = getDateParamFromAdvancedOption(option);

		return value ? `${fieldName}:${value}` : undefined;
	}

	if (fieldName === 'resolution') {
		const value = getRangeParamFromResolution(name, rangeValue);

		return value ? `${fieldName}:${value}` : undefined;
	}

	if (fieldName === 'duration') {
		const value = getRangeParamFromDuration(name, rangeValue);

		return value ? `${fieldName}:${value}` : undefined;
	}

	if (fieldName === 'dpi') {
		return `dpi:[${name} TO *]`;
	}

	return `${fieldName}:${name}`;
};

// ---------------------------------------------------------------------------
//  Search-term clauses — the one builder for both the dropdown query
//  (`buildQueryWithSearchOptions`) and `searchTerms` below. Mirrors
//  paramount's `SEARCH_OPTION_DEFINITIONS[*].buildQueryClause` and
//  `buildSearchTermQueryClause`.
// ---------------------------------------------------------------------------

const splitWords = (term: string): string[] => term.trim().split(/\s+/);

/** Context for AI: Solr clause per search option; keyed by the enum, so a new option doesn't compile until it has one. */
export const SEARCH_OPTION_CLAUSE_BUILDERS: Record<
	SearchOptionType,
	(searchTerm: string, searchOperator: SearchOperator) => string
> = {
	[SearchOptionType.Title]: (searchTerm, searchOperator) =>
		splitWords(searchTerm)
			.map((t) => `name_ci:*${t}*`)
			.join(` ${searchOperator} `),
	[SearchOptionType.Description]: (searchTerm, searchOperator) => {
		const wordClauses = splitWords(searchTerm).map(
			(t) => `description:*${t}* OR description:${t}`,
		);

		// Each word expands to two OR'd alternatives. With several words, each
		// pair is parenthesised so the operator joining the words can't rebind
		// the inner ORs.
		return wordClauses.length > 1
			? wordClauses.map((clause) => `(${clause})`).join(` ${searchOperator} `)
			: wordClauses[0];
	},
};

/** One parenthesised clause per selected option, in `SearchOptionType` order. */
export const buildSearchOptionClauses = (
	searchTerm: string,
	searchOptions: SelectedSearchOption[],
	searchOperator: SearchOperator,
): string[] =>
	Object.values(SearchOptionType)
		.filter((key) => searchOptions.some((o) => o.key === key))
		.map((key) => `(${SEARCH_OPTION_CLAUSE_BUILDERS[key](searchTerm, searchOperator)})`);

// An applied term is wrapped even without options, because the applied terms
// are joined with ` AND `.
const buildSearchTermQueryClause = (
	searchTerm: string,
	searchOptions: SelectedSearchOption[],
	searchOperator: SearchOperator,
): string => {
	if (searchOptions.length === 0) {
		return `(${searchTerm})`;
	}

	if (!searchTerm.trim()) {
		return '';
	}

	const chipClauses = buildSearchOptionClauses(searchTerm, searchOptions, searchOperator);
	const termClauses = chipClauses.length > 0 ? chipClauses : [`(${searchTerm})`];

	return termClauses.join(' AND ');
};

// ---------------------------------------------------------------------------
//  Per-type builders  (mirror the helpers in `apis/utils.api.ts`)
// ---------------------------------------------------------------------------

const buildSmartFiltersParams = (options: FilteringOption[]): string[] => {
	const out: string[] = [];

	for (const o of options) {
		if (o.type !== FilteringOptionType.smartFilter) {
			continue;
		}

		if (isDateSmartFilter(o.name)) {
			continue;
		}

		if (o.isBulkSearch) {
			continue;
		}

		if (typeof o.name !== 'string') {
			continue;
		}

		out.push(`metaproperty_${o.fieldName}:${formatSmartFilterValue(o.name)}`);
	}

	return out;
};

const buildStatelessTagsParams = (options: FilteringOption[]): string[] => {
	const manual: string[] = [];
	const auto: string[] = [];

	for (const o of options) {
		if (o.type !== FilteringOptionType.tagsFilter) {
			continue;
		}

		if (typeof o.name !== 'string') {
			continue;
		}

		if (o.fieldName === 'manualTags') {
			manual.push(o.name);
		} else if (o.fieldName === 'autoTags') {
			auto.push(o.name);
		}
	}

	const out: string[] = [];

	if (manual.length) {
		out.push(`tags:(${manual.map(formatTagValue).join(' AND ')})`);
	}

	if (auto.length) {
		out.push(`autotags:(${auto.map(formatTagValue).join(' AND ')})`);
	}

	return out;
};

const buildBulkSearchParams = (options: FilteringOption[]): string[] => {
	// Latest value wins per metaproperty (Map preserves insertion order; resetting
	// the key on every hit keeps "latest wins" semantics).
	const bulk = new Map<string, string>();

	for (const o of options) {
		if (!o.isBulkSearch) {
			continue;
		}

		if (typeof o.name !== 'string') {
			continue;
		}

		const built = buildBulkSearchString(o.fieldName, o.name);

		if (built) {
			bulk.set(formatMetapropertyName(o.fieldName), built);
		}
	}

	return Array.from(bulk.values());
};

const buildDateSmartFilterParams = (options: FilteringOption[]): string[] => {
	const out: string[] = [];

	for (const o of options) {
		if (!isDateSmartFilter(o.name)) {
			continue;
		}

		out.push(buildDateMetafieldString(o.fieldName, o.name));
	}

	return out;
};

const buildStatelessAdvancedFiltersParams = (options: FilteringOption[]): string[] => {
	// Multi-filter options for the same field are grouped into one
	// `<field>:(v1 OR v2)` clause, so the values after the first stay bound to
	// the field. Only plain `<field>:<name>` values group; a later range or
	// date value replaces the field's clause.
	const byField = new Map<string, { param: string; multifilterValues: string[] | null }>();

	for (const o of options) {
		if (o.type !== FilteringOptionType.advancedFilter) {
			continue;
		}

		if (typeof o.name !== 'string') {
			continue;
		}

		const param = advancedOptionToQueryParam(o);

		if (typeof param !== 'string') {
			continue;
		}

		const isSimple = param === `${o.fieldName}:${o.name}`;
		const previous = byField.get(o.fieldName);

		if (previous?.multifilterValues && o.isMultifilter && isSimple) {
			previous.multifilterValues.push(o.name);
			continue;
		}

		byField.set(o.fieldName, {
			param,
			multifilterValues: o.isMultifilter && isSimple ? [o.name] : null,
		});
	}

	return Array.from(byField.entries()).map(([fieldName, { param, multifilterValues }]) =>
		multifilterValues && multifilterValues.length > 1
			? `${fieldName}:(${multifilterValues.join(' OR ')})`
			: param,
	);
};

const buildStatelessColorFilterParams = (options: FilteringOption[]): string[] => {
	const color = options.find((o) => o.type === FilteringOptionType.colorFilter);

	if (!color || typeof color.name !== 'string' || !color.name.length) {
		return [];
	}

	return [`color:${color.name}`];
};

// Similarity has two shapes in session storage:
//   - `similarToAssetIds`: persisted as one similarity option per asset id,
//     with `name = <assetId>`. We collect them all and emit a single
//     `id:(id1 OR id2 …)` clause (mirrors paramount's
//     `getAssetIdsForSimilarityFiltering`).
//   - Other similarity fieldNames (`similarToURL`, `similarToText`,
//     `similarToImageData`): emit `<fieldName>:<name>` so the search
//     service runs the visual-similarity backend on the given input.
const buildSimilarityParams = (options: FilteringOption[]): string[] => {
	const out: string[] = [];
	const assetIds: string[] = [];

	for (const o of options) {
		if (o.type !== FilteringOptionType.similarityFilter) {
			continue;
		}

		if (typeof o.name !== 'string' || !o.name.length) {
			continue;
		}

		if (o.fieldName === SIMILAR_TO_ASSET_IDS) {
			assetIds.push(o.name);
			continue;
		}

		out.push(`${o.fieldName}:${o.name}`);
	}

	if (assetIds.length > 0) {
		out.push(`id:(${assetIds.join(' OR ')})`);
	}

	return out;
};

const buildSearchTermsParam = (
	options: FilteringOption[],
	searchOperator: SearchOperator,
): string | undefined => {
	const clauses: string[] = [];

	for (const o of options) {
		if (o.type !== FilteringOptionType.searchTerm) {
			continue;
		}

		const term = (o.searchTerm ?? (typeof o.name === 'string' ? o.name : '')).trim();

		if (!term) {
			continue;
		}

		const clause = buildSearchTermQueryClause(term, o.searchOptions ?? [], searchOperator);

		if (clause) {
			clauses.push(clause);
		}
	}

	if (!clauses.length) {
		return undefined;
	}

	return clauses.join(' AND ');
};

// ---------------------------------------------------------------------------
//  Public API
// ---------------------------------------------------------------------------

/**
 * Reads the active filters for the current page from sessionStorage.
 * The same keying paramount uses (`activeFilters` → pageTypeKey).
 *
 * Always returns an array (empty on missing / malformed data).
 */
export const getActiveFiltersFromSessionStorage = (): FilteringOption[] => {
	try {
		const raw = sessionStorage.getItem(ACTIVE_FILTERS_LOCAL_STORAGE_KEY);

		if (!raw) {
			return [];
		}

		const parsed: unknown = JSON.parse(raw);

		if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
			return [];
		}

		const key = getActiveFiltersPageTypeKey();
		const value = (parsed as Record<string, unknown>)[key];

		if (!Array.isArray(value)) {
			return [];
		}

		return value as FilteringOption[];
	} catch {
		return [];
	}
};

/**
 * Pure mapper: turns the active `FilteringOption[]` into the POST body shape
 * expected by `search/assets` endpoints.
 *
 * Output order mirrors paramount's `getFiltersParamsFromSession`:
 *   smart, tags, bulk-search, date-smart, advanced, color — then similarity,
 *   which paramount doesn't send on this path.
 */
export const mapFilteringOptionsToFiltersParam = (
	filteringOptions: FilteringOption[],
	searchOperator: SearchOperator,
): FiltersParameterForPostRequest => {
	const filters: string[] = [
		...buildSmartFiltersParams(filteringOptions),
		...buildStatelessTagsParams(filteringOptions),
		...buildBulkSearchParams(filteringOptions),
		...buildDateSmartFilterParams(filteringOptions),
		...buildStatelessAdvancedFiltersParams(filteringOptions),
		...buildStatelessColorFilterParams(filteringOptions),
		...buildSimilarityParams(filteringOptions),
	];

	const searchTerms = buildSearchTermsParam(filteringOptions, searchOperator);

	if (searchTerms) {
		return { filters, searchTerms };
	}

	return { filters };
};

/** Convenience: read from sessionStorage and map in a single call. */
export const readFiltersParamFromSessionStorage = (
	searchOperator: SearchOperator,
): FiltersParameterForPostRequest => {
	return mapFilteringOptionsToFiltersParam(getActiveFiltersFromSessionStorage(), searchOperator);
};
